import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SupportButtons } from "@/components/Brand";
import { API, errMsg, fmtDate, PLATFORM_INFO, SOURCES } from "@/lib/api";
import { usePanel } from "./PanelContext";

function PlatformCard({ k, data, token, onSaved }) {
  const { api } = usePanel();
  const info = PLATFORM_INFO[k];
  const [f, setF] = useState({ enabled: data.enabled, api_key: data.api_key, api_secret: data.api_secret, restaurant_id: data.restaurant_id });
  const url = `${API}/webhooks/${k}/${token}`;
  const save = async () => {
    try { await api.put(`/integrations/${k}`, f); toast.success(`${info.name} ayarları kaydedildi`); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-3xl bg-white border overflow-hidden" data-testid={`integration-card-${k}`}>
      <div className="p-5 text-white flex items-center gap-3" style={{ background: info.color }}>
        <span className="h-11 w-11 rounded-2xl bg-white/20 grid place-items-center font-display text-xl font-black">{info.letter}</span>
        <div className="flex-1"><p className="font-display font-bold text-lg">{info.name}</p>
          <p className="text-xs text-white/85" data-testid={`integration-activation-${k}`}>{data.active ? `Yıllık aktivasyon: ${fmtDate(data.active_until)}'e kadar` : "Yıllık aktivasyon yok"}</p></div>
        <Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid={`integration-enabled-switch-${k}`} />
      </div>
      <div className="p-5 space-y-3">
        <div><Label>Restoran / Mağaza ID</Label><Input value={f.restaurant_id} onChange={(e) => setF({ ...f, restaurant_id: e.target.value })} data-testid={`integration-restaurant-id-${k}`} /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>API Anahtarı</Label><Input value={f.api_key} onChange={(e) => setF({ ...f, api_key: e.target.value })} data-testid={`integration-api-key-${k}`} /></div>
          <div><Label>API Şifresi</Label><Input type="password" value={f.api_secret} onChange={(e) => setF({ ...f, api_secret: e.target.value })} data-testid={`integration-api-secret-${k}`} /></div>
        </div>
        <div>
          <Label>Sipariş bağlantı adresi (webhook)</Label>
          <div className="flex gap-2"><Input readOnly value={url} className="text-xs font-mono-num" data-testid={`integration-webhook-${k}`} />
            <Button size="icon" variant="outline" onClick={() => { navigator.clipboard?.writeText(url); toast.success("Kopyalandı"); }} data-testid={`integration-webhook-copy-${k}`}><Copy className="h-4 w-4" /></Button></div>
        </div>
        <Button className="w-full rounded-full bg-[#0F1F45]" onClick={save} data-testid={`integration-save-btn-${k}`}>Kaydet</Button>
        {!data.active && <p className="text-xs text-amber-700 bg-amber-50 rounded-xl p-3">Siparişlerin otomatik düşmesi için yıllık aktivasyon gerekir. Aktivasyon için destek hattına ulaşın.</p>}
      </div>
    </div>
  );
}

const GUIDES = [
  { k: "trendyol", title: "Trendyol Go Yemek nasıl bağlanır?", steps: [
    <>partner.trendyol.com adresinden restoran satıcı panelinize <b>yetkili (admin) hesabınızla</b> girin.</>,
    <>Sağ üstte restoran adınıza tıklayın → <b>Hesap Bilgilerim → Entegrasyon Bilgileri</b>.</>,
    <><b>“Entegrasyon Süreci”</b> anahtarını <b>Aktif</b> yapın (kapalıysa bilgiler görünmez).</>,
    <><b>Satıcı ID</b>'yi “Restoran / Mağaza ID” alanına, <b>API Key</b> ve <b>API Secret</b>'ı aşağıdaki Trendyol kartına yapıştırın.</>,
  ] },
  { k: "yemeksepeti", title: "Yemeksepeti nasıl bağlanır?", steps: [
    <>Yemeksepeti <b>Partner Portal</b>'a girin → <b>Shop Integrations (Mağaza Entegrasyonları)</b>. Menü yoksa Yemeksepeti hesap yöneticinizden POS entegrasyonunu açtırın.</>,
    <><b>Settings → Vendor Identifier</b> ekranındaki <b>Vendor (Restoran) ID</b>'yi “Restoran / Mağaza ID” alanına yazın.</>,
    <>Aynı bölümde oluşturduğunuz <b>Client ID</b>'yi “API Anahtarı”, <b>Client Secret</b>'ı “API Şifresi” alanına yapıştırın.</>,
  ] },
  { k: "migros", title: "Migros Yemek nasıl bağlanır?", steps: [
    <>Migros Yemek <b>restoran/partner panelinize</b> girin ve entegrasyon (API) bilgilerinizi talep edin; gerekirse Migros Yemek destek hattından isteyin.</>,
    <>Size verilen <b>Restoran ID</b>, <b>API anahtarı</b> ve <b>şifreyi</b> aşağıdaki Migros kartına girin.</>,
  ] },
];

function ConnectGuide() {
  return (
    <div className="rounded-3xl bg-white border p-6 space-y-4" data-testid="integrations-guide">
      <div>
        <p className="font-display font-bold text-lg">Platformları siteye bağlama rehberi</p>
        <p className="text-sm text-slate-500">Her platform için 3 adım: <b>1)</b> platform panelinden API bilgilerini alın, <b>2)</b> aşağıdaki karta girip anahtarı açın ve <b>Kaydet</b>'e basın, <b>3)</b> yıllık aktivasyon için destek hattına yazın. Aktivasyon tamamlanınca siparişler “Siparişler” ekranına otomatik düşer.</p>
      </div>
      <div className="grid lg:grid-cols-3 gap-3 items-start">
        {GUIDES.map((g) => (
          <details key={g.k} className="rounded-2xl border p-4 open:bg-slate-50" data-testid={`integration-guide-${g.k}`}>
            <summary className="cursor-pointer font-semibold flex items-center gap-2">
              <span className="h-6 w-6 rounded-lg grid place-items-center text-white text-xs font-black" style={{ background: PLATFORM_INFO[g.k].color }}>{PLATFORM_INFO[g.k].letter}</span>
              {g.title}
            </summary>
            <ol className="mt-3 list-decimal pl-5 space-y-1.5 text-sm text-slate-600">
              {g.steps.map((s, i) => <li key={i}>{s}</li>)}
              <li>Kartta <b>anahtarı açın</b>, <b>Kaydet</b>'e basın ve yıllık aktivasyon için destek hattına yazın.</li>
            </ol>
          </details>
        ))}
      </div>
      <p className="text-xs text-slate-500">“Sipariş bağlantı adresi (webhook)” kartlarda yer alır; entegrasyon firmanız / destek ekibimiz siparişleri bu adrese iletir. API bilgilerinizi kimseyle paylaşmayın.</p>
    </div>
  );
}

export default function Integrations() {
  const { api } = usePanel();
  const [data, setData] = useState(null);
  const load = () => api.get("/integrations").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <p className="text-slate-500">Yükleniyor...</p>;
  return (
    <div className="space-y-6 rise" data-testid="integrations-page">
      <div><h1 className="text-3xl font-extrabold">Entegrasyonlar & Sipariş Yöntemleri</h1>
        <p className="text-sm text-slate-500">Platform siparişleri aktivasyon ve bayi API bilgileriyle otomatik düşer; dilerseniz “Yeni Sipariş” ekranından platform seçerek elle de girebilirsiniz.</p></div>
      <div className="flex flex-wrap gap-2">
        {Object.values(SOURCES).map((s) => <span key={s} className="rounded-full bg-white border px-4 py-1.5 text-sm font-medium">{s}</span>)}
      </div>
      <ConnectGuide />
      <div className="grid lg:grid-cols-3 gap-5">
        {Object.keys(PLATFORM_INFO).map((k) => <PlatformCard key={k} k={k} data={data.platforms[k]} token={data.webhook_token} onSaved={load} />)}
      </div>
      <div className="rounded-3xl bg-white border p-6 flex flex-wrap items-center gap-4">
        <div className="mr-auto"><p className="font-display font-bold">Yıllık aktivasyon</p><p className="text-sm text-slate-500">Her platform için yıllık aktivasyon talebinizi iletin.</p></div>
        <SupportButtons compact prefix="integrations-support" />
      </div>
    </div>
  );
}
